import type { OpenAPI } from "simplydo/interfaces";

import SearchParams from "./searchParams";

export type NotificationContext = Record<
  string,
  string | number | boolean | Record<string, never> | undefined
>;

/** Runtime event shape used for notification navigation. */
export type NotificationEvent = {
  user?: string;
  comment?: string;
  commentId?: string;
  commentText?: string;
  isFollower?: boolean;
  message?: string;
  createdAt?: string;
  achievement?: string;
  invitation?: string;
  threadId?: string;
  messageId?: string;
  idea?: string;
  challenge?: string;
  group?: string;
  discussion?: string;
  calendar?: string;
  submittedChallengeId?: string;
};

/** Broader notification shape for navigation than the OpenAPI oneOf schema. */
export type NotificationNavSource = {
  notificationType?: string;
  events?: NotificationEvent[];
  idea?: string;
  challenge?: string;
  group?: string;
  discussion?: string;
  ideaBusinessProfile?: string;
  ownerChallenge?: { _id?: string };
  context?: NotificationContext;
};

export type Notification = NotificationNavSource;
export type OpenAPINotification = OpenAPI.Schemas["Notification"];

type NotificationLocationOptions = { pathname?: string };

const MENTIONED_ENTITY_PATHS: Record<
  string,
  { segment: string; id: "discussion" | "idea" }
> = {
  mentionedDiscussionComment: { segment: "discussions", id: "discussion" },
  mentionedIdeaComment: { segment: "ideas", id: "idea" },
};

const SOURCE_ID_FIELDS = [
  "idea",
  "challenge",
  "group",
  "discussion",
  "ideaBusinessProfile",
] as const;

function parseJsonField<T>(value: unknown): T | undefined {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }
  if (typeof value === "string") {
    try {
      return JSON.parse(value) as T;
    } catch {
      return undefined;
    }
  }
  return value as T;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function pathWithSearch(
  path: string,
  params: Record<string, string | undefined>,
) {
  return `${path}${new SearchParams(params).toSafeString()}`;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

type SourceOrEventIdKey = "idea" | "challenge" | "group" | "discussion";

/** Prefer the event value when present; only resolve if the key exists on source. */
function idFromSourceOrEvent(
  source: NotificationNavSource,
  event: NotificationEvent,
  key: SourceOrEventIdKey,
): string | undefined {
  if (!(key in source)) return undefined;
  return asString(key in event ? event[key] : source[key]);
}

function resolveNavIds(
  source: NotificationNavSource,
  event: NotificationEvent = {},
) {
  return {
    ideaId: idFromSourceOrEvent(source, event, "idea"),
    challengeId: idFromSourceOrEvent(source, event, "challenge"),
    groupId: idFromSourceOrEvent(source, event, "group"),
    discussionId: idFromSourceOrEvent(source, event, "discussion"),
    threadId: asString(event.threadId),
    commentId: asString(event.commentId),
    comment: asString(event.comment),
    calendarId: asString(event.calendar),
    submittedChallengeId: asString(event.submittedChallengeId),
  };
}

function resolveIds(source: NotificationNavSource) {
  const events = source.events ?? [];
  return { events, ...resolveNavIds(source, events[0] ?? {}) };
}

export function parseNotificationNavSource(
  data: unknown,
): NotificationNavSource | null {
  const record = asRecord(data);
  if (
    !record?.notificationType ||
    typeof record.notificationType !== "string"
  ) {
    return null;
  }

  const source: NotificationNavSource = {
    notificationType: record.notificationType,
    events: parseJsonField<NotificationEvent[]>(record.events) ?? [],
  };

  for (const key of SOURCE_ID_FIELDS) {
    const value = record[key];
    if (typeof value === "string" && value) {
      source[key] = value;
    }
  }

  const context = parseJsonField<NotificationContext>(record.context);
  if (context) {
    source.context = context;
  }

  const ownerChallenge = parseJsonField<{ _id?: string }>(
    record.ownerChallenge,
  );
  if (ownerChallenge) {
    source.ownerChallenge = ownerChallenge;
  }

  if (!Array.isArray(source.events)) {
    source.events = [];
  }

  return source;
}

export function getNotificationLocation(
  source: NotificationNavSource,
  options: NotificationLocationOptions = {},
): string | null {
  const { notificationType } = source;
  if (!notificationType) return null;

  const {
    events,
    ideaId,
    challengeId,
    groupId,
    discussionId,
    threadId,
    commentId,
    comment,
    calendarId,
    submittedChallengeId,
  } = resolveIds(source);

  switch (notificationType) {
    case "messaged":
      return threadId ? `/messages/${threadId}` : null;

    case "ideaBusinessProfileInvited":
      return pathWithSearch(options.pathname ?? "", {
        businessProfileId: source.ideaBusinessProfile,
      });

    case "ideaCommented":
    case "ideaCommentReply":
    case "reactedIdeaComment":
      return ideaId ?
          pathWithSearch(`/ideas/${ideaId}`, { comment: commentId })
        : null;

    case "collaboratorAdded":
    case "ideaVoted":
    case "ideaInvited":
    case "aiIdeaFeedback":
    case "aiIdeaAnonymityCheck":
      return ideaId ? `/ideas/${ideaId}` : null;

    case "stageChanged":
    case "closingSoon":
    case "challengeClosed":
    case "requestOpenChallenge":
    case "challengeTranslate":
      return challengeId ? `/challenges/${challengeId}` : null;

    case "aiChallengeFormat":
      return challengeId ? `/challenges/${challengeId}/settings/format` : null;

    case "challengeInnointelSearch":
      if (source.context?.searchId) {
        return `/innointel/searches/${source.context.searchId}`;
      }
      return challengeId ? `/challenges/${challengeId}` : null;

    case "challengeAddedToCalendarEvent":
      return challengeId ? `/challenges/${challengeId}/planner` : null;

    case "groupAddedToCalendarEvent":
      if (!groupId) return null;
      return calendarId ?
          `/groups/${groupId}/calendars/${calendarId}`
        : `/groups/${groupId}`;

    case "assessorAssigned":
      return challengeId ? `/challenges/${challengeId}/assessments` : null;

    case "ideaSubmitted":
    case "ideaDeadlineSet":
    case "ideaDeadlineApproaching":
      if (events.length > 1) {
        return submittedChallengeId ?
            `/challenges/${submittedChallengeId}/ideas`
          : null;
      }
      return ideaId ? `/ideas/${ideaId}` : null;

    case "reactedDiscussionComment":
    case "discussionCommentReply":
    case "discussionPostRequiresApproval":
    case "discussionPosted":
    case "reactedGroupDiscussion":
    case "repliedGroupDiscussion":
    case "mentionedGroupDiscussion":
      if (!discussionId) {
        return groupId ? `/groups/${groupId}` : null;
      }
      return pathWithSearch(`/discussions/${discussionId}`, {
        comment: commentId,
      });

    case "discussionPollVoted":
      return discussionId ?
          pathWithSearch(`/discussions/${discussionId}`, { comment: comment })
        : null;

    case "mentionedDiscussionComment":
    case "mentionedIdeaComment": {
      const mentioned = MENTIONED_ENTITY_PATHS[notificationType];
      const entityId = mentioned ? source[mentioned.id] : undefined;
      if (!mentioned || !entityId || typeof entityId !== "string") {
        return null;
      }
      return pathWithSearch(`/${mentioned.segment}/${entityId}`, {
        comment: commentId,
      });
    }

    case "mentionedIdeaProjectComment":
    case "projectIdeaCommented":
    case "projectIdeaReplied":
      return challengeId ?
          pathWithSearch(`/challenges/${challengeId}/board`, {
            idea: ideaId,
            comment: commentId,
          })
        : null;

    case "projectIdeaMoved":
    case "projectAssignedAssignee":
    case "projectAssignedManager":
      return challengeId ?
          pathWithSearch(`/challenges/${challengeId}/board`, { idea: ideaId })
        : null;

    case "unlockedAchievement":
      return "/achievements";

    case "pollClosed":
      return source.context?.pollId ?
          `/admin/polls/${source.context.pollId}/results`
        : null;

    case "groupJoinRequested":
    case "groupInvited":
      return "/";

    case "groupUserJoined":
      return groupId ? `/groups/${groupId}` : null;

    case "announcementReport":
      return typeof source.context?.logLink === "string" ?
          source.context.logLink
        : null;

    default:
      return null;
  }
}
